import qs from 'query-string';
import {
  NavigateFunction,
  Params,
  useLocation,
  useNavigate,
  useParams,
  Location,
} from 'react-router-dom';

export type WithRouterProps = {
  location: Location;
  navigate: NavigateFunction;
  params: Params<string>;
  query: qs.ParsedQuery;
};

/**
 * A higher-order component that injects navigation and location parameters into a component.
 *
 * @param {React.ComponentType<P & WithRouterProps>} Component - The component to wrap
 * @returns {React.ComponentType<Omit<P, keyof WithRouterProps>>} A new component with navigation and location parameters injected
 *
 * @example
 * const MyComponent = withRouter((props) => {
 *   return <div>My Component</div>;
 * });
 *
 * <MyComponent />
 *
 */
const withRouter =
  <P extends object>(Component: React.ComponentType<P & WithRouterProps>) =>
  (props: Omit<P, keyof WithRouterProps>) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const query = qs.parse(location.search);

    return (
      <Component
        {...(props as P)}
        location={location}
        navigate={navigate}
        params={params}
        query={query}
      />
    );
  };

export default withRouter;
